"use client";

import { FEATURE_FLAGS } from "@cede/types";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useUserStats, useHubUser, useTickets } from "@/hooks";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { cn } from "@/lib/utils";
import { formatStatValue } from "@/utils/general";

interface EarningsDisplayTMAProps {
  className?: string;
}

export const EarningsDisplay = ({ className }: EarningsDisplayTMAProps) => {
  const { authenticated, isLoadingHubUser } = useHubUser();
  const isQuestsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.QUESTS_HUB_ENABLE);

  const { isTMA } = useTelegramContext();
  // Only fetch tickets when quests feature is enabled and user is authenticated
  const { tickets: userTickets, isLoading: isLoadingTickets } = useTickets({
    enabled: isQuestsEnabled && authenticated && isTMA,
  });

  const { stats, isLoading } = useUserStats();

  if (!authenticated) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center text-center gap-2 min-h-32 bg-cede-bg-component rounded-lg p-4",
          className,
        )}
      >
        <div className="h6 text-cede-primary font-bold">Welcome to Cede Hub!</div>
        <div className="body1 text-cede-text-primary">
          {" "}
          To participate in Trade Campaigns,
          <br />
          please connect your wallet.
        </div>
      </div>
    );
  }

  if (isLoading || isLoadingHubUser || isLoadingTickets) {
    return (
      <div className={cn("flex flex-col gap-3 min-h-32", className)}>
        {/* Claimable Now Skeleton */}
        <div className="flex flex-col text-center gap-1">
          <div className="h-11 bg-cede-skeleton rounded w-40 mx-auto animate-pulse"></div>
          <div className="h-4 bg-cede-skeleton rounded w-24 mx-auto animate-pulse"></div>
        </div>
        {/* Wheel Tickets Skeleton */}
        <div className="flex flex-row gap-2 justify-around">
          {isQuestsEnabled && (
            <div className="flex flex-col text-center gap-1">
              <div className="h-8 bg-cede-skeleton rounded w-28 mx-auto animate-pulse"></div>
              <div className="h-4 bg-cede-skeleton rounded w-20 mx-auto animate-pulse"></div>
            </div>
          )}
          {/* Total Earned Skeleton */}
          <div className="flex flex-col text-center gap-1">
            <div className="h-8 bg-cede-skeleton rounded w-28 mx-auto animate-pulse"></div>
            <div className="h-4 bg-cede-skeleton rounded w-20 mx-auto animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3 min-h-32", className)}>
      {/* Token Balance Display */}
      <div className="text-center">
        <div className="relative text-center">
          <div className="relative z-top">
            <div className="h4 text-cede-primary font-bold">{formatStatValue(stats?.claimableNow?.value, true)}</div>
            <div className="body2 text-cede-text-secondary">Claimable Now</div>
          </div>
        </div>
      </div>

      {/* Wheel Tickets Stats Row */}
      <div className="flex flex-row gap-2 justify-around">
        {isQuestsEnabled && (
          <div className="flex justify-center">
            <div className="text-center">
              <div className="h6 text-cede-text-primary font-semibold">{userTickets || 0}</div>
              <div className="body2 text-cede-text-secondary">Wheel Spins</div>
            </div>
          </div>
        )}

        {/* Total Earned Stats Row */}
        <div className="flex justify-center">
          <div className="text-center">
            <div className="h6 text-cede-text-primary font-semibold">
              {formatStatValue(stats?.totalVolumeRewarded?.value, true)}
            </div>
            <div className="body2 text-cede-text-secondary">Total Earned</div>
          </div>
        </div>
      </div>
    </div>
  );
};
